import type { CategoryTagsResponse } from '@contracts/order/OrderCategoriesTagsResponse';
import AddRounded from '@mui/icons-material/AddRounded';
import Skeleton from '@mui/material/Skeleton';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { ButtonLink } from '@src/common/components/button/ButtonLink';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { Toggle } from '@src/common/components/Toggle';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { CountBadge } from '@src/orders/CountBadge';
import {
  INDEX_BY_ORDER_TAB,
  ORDER_TAB_BY_INDEX,
  OrderTab,
} from '@src/orders/domain/OrderTab';
import { useAssignedToMeState } from '@src/orders/hooks/useAssignedToMeState';
import { useCurrentTab } from '@src/orders/hooks/useCurrentOrdersTabs';
import { useCurrentPage } from '@src/orders/hooks/useCurrentPage';
import { useGetOrderPagination } from '@src/orders/hooks/useGetOrderPagination';
import { useOrderTabs } from '@src/orders/hooks/useOrderTabs';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import { OrderTable } from '@src/orders/OrderTable';
import { TabPanel } from '@src/orders/TabPanel';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

export const OrdersDashboard = () => {
  const { t } = useTranslation();
  const { refreshOrders } = useRefreshOrders();
  const { assignedToMe, setAssignedToMe } = useAssignedToMeState();
  const { orderTabs, isLoading } = useOrderTabs({
    assignedToMe,
  });

  const { currentTab, setCurrentTabOnFirstPage, setCurrentTabAndPage } =
    useCurrentTab();
  const { getOrderPagination } = useGetOrderPagination();
  const { pageSize } = useCurrentPage();

  const refreshOrder = async (orderId: string) => {
    refreshOrders(orderId);
    const newOrderPagination = await getOrderPagination({
      orderId,
      assignedToMe,
      pageSize,
    });
    setCurrentTabAndPage(newOrderPagination.tab, newOrderPagination.page);
  };

  const { user } = useAuthentication();

  return (
    <PageLayout
      title={t('orders.title')}
      rightBlock={
        <ButtonLink
          to={OrdersNavigation.create.PATH}
          startIcon={<AddRounded />}
        >
          {t('newOrder')}
        </ButtonLink>
      }
    >
      <Stack direction="row" justifyContent="space-between">
        <MuiTabs
          variant="fullWidth"
          TabIndicatorProps={{ style: { height: '1px' } }}
          sx={{ marginBottom: 4 }}
          value={INDEX_BY_ORDER_TAB[currentTab]}
          onChange={(_, index) =>
            setCurrentTabOnFirstPage(ORDER_TAB_BY_INDEX[index])
          }
        >
          {orderTabs &&
            Object.entries(orderTabs).map(([tab, tags]) =>
              getTabTitle(tab as OrderTab, tags, t, isLoading),
            )}
        </MuiTabs>

        {!isClientUser(user) && (
          <Toggle
            sx={{ marginBottom: 4 }}
            checked={assignedToMe}
            onToggle={() => setAssignedToMe(!assignedToMe)}
            label={t('orders.assignedToMe')}
          />
        )}
      </Stack>

      <Stack>
        <TabPanel
          key={`order-tab-${currentTab}`}
          index={INDEX_BY_ORDER_TAB[currentTab]}
          value={INDEX_BY_ORDER_TAB[currentTab]}
        >
          <OrderTable
            tab={currentTab}
            assignedToMe={assignedToMe}
            onOrderChange={refreshOrder}
          />
        </TabPanel>
      </Stack>
    </PageLayout>
  );
};

const TabStyle = css(() => ({
  width: '165px',
  maxWidth: '165px',
}));

const Dot = css((theme) => ({
  width: '6px',
  height: '6px',
  borderRadius: '100%',
  background: theme.palette.error.error,
}));

const getTabTitle = (
  tab: OrderTab,
  { requiresAttention, totalCount }: CategoryTagsResponse,
  t: TFunction,
  isLoading: boolean,
) => {
  return (
    <MuiTab
      className={TabStyle}
      label={
        <Stack gap={theme.spacing['2']} direction="row" alignItems="center">
          {t(`orders.${tab}`)}
          {tab !== OrderTab.Archived &&
            (isLoading ? (
              <Skeleton
                variant="rectangular"
                width={40}
                height={20}
                sx={{ borderRadius: '10px' }}
              />
            ) : (
              <CountBadge count={totalCount} light />
            ))}
          {requiresAttention && (
            <div
              className={Dot}
              role="img"
              aria-label={t('orders.requiresAttention')}
            />
          )}
        </Stack>
      }
      id={`horizontal-tab-${tab}`}
      key={`horizontal-tab-${tab}`}
      aria-controls={`horizontal-tabpanel-${tab}`}
    />
  );
};
